import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FadeUp, GoldLineDraw } from "@/components/motion/Reveal";
import { Calculator, ArrowRight, Lock, Check, Shield } from "lucide-react";
import { api } from "@/lib/api";

const CURRENCIES = [
  { code: "GBP", symbol: "£" },
  { code: "EUR", symbol: "€" },
  { code: "USD", symbol: "$" },
  { code: "ZAR", symbol: "R" },
];

function fmt(v, sym) {
  return `${sym}${Math.round(v || 0).toLocaleString("en-GB")}`;
}

export default function MortgageCalculator() {
  const [price, setPrice] = useState(100000);
  const [cur, setCur] = useState("GBP");
  const [term, setTerm] = useState(10);
  const [depositPct, setDepositPct] = useState(15);
  const [ratePct, setRatePct] = useState(3.0); // illustrative only — user-adjustable
  const [lockBusy, setLockBusy] = useState(false);
  const [lockRes, setLockRes] = useState(null);
  const symbol = CURRENCIES.find((c) => c.code === cur)?.symbol || "£";

  // Estimated extras — clear, illustrative, no hidden line
  const extras = useMemo(() => {
    const p = parseFloat(price) || 0;
    const legal = p * 0.015;      // ~1.5% legal & registration
    const stamp = p * 0.01;       // ~1% stamp/transfer
    const admin = 250;            // flat admin
    return { legal, stamp, admin, total: legal + stamp + admin };
  }, [price]);

  const { deposit, financed, monthly, totalInterest, totalOwnership } = useMemo(() => {
    const p = parseFloat(price) || 0;
    const dep = p * (depositPct / 100);
    const fin = p - dep;
    const rate = (ratePct || 0) / 100;
    const totalInt = fin * rate * term;
    const m = (fin + totalInt) / (term * 12);
    const totalOwn = dep + fin + totalInt + extras.total; // price + interest + extras
    return { deposit: dep, financed: fin, monthly: m, totalInterest: totalInt, totalOwnership: totalOwn };
  }, [price, term, depositPct, ratePct, extras.total]);

  const lock = async () => {
    setLockBusy(true);
    setLockRes(null);
    try {
      const { data } = await api.post("/quote-locks", {
        property_price: parseFloat(price) || 0,
        currency: cur, term_years: term,
      });
      setLockRes(data);
      toast.success(`Price locked for 90 days · Ref ${data.lock_ref}`);
    } catch (e) {
      toast.error("Could not create lock — please try again.");
    } finally { setLockBusy(false); }
  };

  return (
    <section className="py-20 md:py-28" data-testid="mortgage-calculator-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <FadeUp><div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Illustrative Cost Estimator</span></div></FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[54px] leading-[1.02] mv-green-text mt-3">
                See a sample breakdown — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>your actual numbers live in your project contract.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-4 text-[15px] max-w-xl" style={{ color: "var(--mv-muted)" }}>
                Illustrative only — adjust the sliders to explore how deposit, term and rate change a total. This is not an offer, not a financial promotion and not a regulated mortgage quote. Home-ownership instalment plans are set by each local developer partner, disclosed in your individual signed sale agreement, and subject to eligibility and country law. MAVULA Investment Ltd is not an FCA-authorised mortgage lender or credit provider.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}><GoldLineDraw className="mt-5 w-14" /></FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FadeUp>
            <div className="mv-card p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
                  <Calculator size={22}/>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em]" style={{ color: "var(--mv-muted)" }}>Inputs</div>
                  <div className="font-display text-xl mv-green-text">Design your ownership</div>
                </div>
              </div>

              <label className="block">
                <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Currency</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => setCur(c.code)}
                      className={`px-4 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${cur === c.code ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
                      data-testid={`calc-currency-${c.code}`}
                    >{c.symbol} {c.code}</button>
                  ))}
                </div>
              </label>

              <label className="block mt-6">
                <div className="flex justify-between items-baseline">
                  <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Property price</span>
                  <span className="font-display text-xl mv-green-text">{fmt(price, symbol)}</span>
                </div>
                <input
                  type="range" min="30000" max="500000" step="1000" value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="mt-3 w-full accent-[color:var(--mv-green)]"
                  data-testid="calc-price-slider"
                />
                <input
                  type="number" value={price} min="1" onChange={(e) => setPrice(e.target.value)}
                  className="mt-3 w-full px-4 py-2 rounded-xl border mv-hairline text-[15px]"
                  data-testid="calc-price-input"
                />
              </label>

              <div className="block mt-6">
                <div className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Repayment term</div>
                <div className="mt-2 flex gap-2">
                  {[5, 10].map((y) => (
                    <button
                      key={y} onClick={() => setTerm(y)}
                      className={`px-5 py-2 rounded-full text-[13px] font-medium border transition-colors ${term === y ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
                      data-testid={`calc-term-${y}`}
                    >{y}-Year Plan</button>
                  ))}
                </div>
              </div>

              <label className="block mt-6">
                <div className="flex justify-between items-baseline">
                  <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Deposit</span>
                  <span className="font-display text-lg mv-green-text">{depositPct}%</span>
                </div>
                <input
                  type="range" min="10" max="50" step="1" value={depositPct}
                  onChange={(e) => setDepositPct(parseInt(e.target.value))}
                  className="mt-3 w-full accent-[color:var(--mv-green)]"
                  data-testid="calc-deposit-slider"
                />
              </label>

              <label className="block mt-6">
                <div className="flex justify-between items-baseline">
                  <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Illustrative rate (annual)</span>
                  <span className="font-display text-lg mv-green-text">{ratePct.toFixed(1)}%</span>
                </div>
                <input
                  type="range" min="0" max="10" step="0.1" value={ratePct}
                  onChange={(e) => setRatePct(parseFloat(e.target.value))}
                  className="mt-3 w-full accent-[color:var(--mv-green)]"
                  data-testid="calc-rate-slider"
                />
                <div className="mt-2 text-[11.5px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
                  For illustration only — the actual rate on any developer plan is set by that project and disclosed in your signed sale agreement.
                </div>
              </label>

              {/* Total cost breakdown */}
              <div className="mt-8 rounded-2xl p-4 mv-hairline border" style={{ background: "var(--mv-bg)" }}>
                <div className="text-[11px] uppercase tracking-[0.22em] mv-green-text">Total cost breakdown</div>
                <ul className="mt-3 text-[13.5px] space-y-1.5">
                  <li className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Land / property price</span><span>{fmt(price, symbol)}</span></li>
                  <li className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Legal &amp; registration (~1.5%)</span><span>{fmt(extras.legal, symbol)}</span></li>
                  <li className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Stamp / transfer (~1%)</span><span>{fmt(extras.stamp, symbol)}</span></li>
                  <li className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Admin fee (flat)</span><span>{fmt(extras.admin, symbol)}</span></li>
                  <li className="flex justify-between"><span style={{ color: "var(--mv-muted)" }}>Interest over {term} yrs</span><span>{fmt(totalInterest, symbol)}</span></li>
                  <li className="flex justify-between pt-2 mt-2 border-t mv-hairline font-semibold mv-green-text"><span>Total cost of ownership</span><span>{fmt(totalOwnership, symbol)}</span></li>
                </ul>
                <div className="mt-3 text-[11.5px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
                  Illustrative — country-specific fees confirmed on your official schedule.
                </div>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <motion.div layout className="mv-card p-6 md:p-8 h-full flex flex-col" style={{ background: "linear-gradient(135deg, rgba(10,59,40,0.05), rgba(212,175,55,0.10))" }}>
              <div className="text-[11px] uppercase tracking-[0.24em]" style={{ color: "var(--mv-gold)" }}>Your Plan</div>
              <div className="font-display text-[46px] md:text-[56px] mv-green-text leading-none tracking-tight mt-2">
                {fmt(monthly, symbol)}<span className="font-accent italic text-2xl md:text-3xl" style={{ color: "var(--mv-gold)" }}> /month</span>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5">
                <div><div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>Property price</div><div className="font-display text-xl mv-green-text mt-0.5">{fmt(price, symbol)}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>Deposit ({depositPct}%)</div><div className="font-display text-xl mv-green-text mt-0.5">{fmt(deposit, symbol)}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>Financed</div><div className="font-display text-xl mv-green-text mt-0.5">{fmt(financed, symbol)}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>Total interest</div><div className="font-display text-xl mv-green-text mt-0.5">{fmt(totalInterest, symbol)}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>Term</div><div className="font-display text-xl mv-green-text mt-0.5">{term} years</div></div>
                <div><div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>Illustrative rate</div><div className="font-display text-xl mv-green-text mt-0.5">{ratePct.toFixed(1)}% / yr</div></div>
              </div>

              {/* 90-day price lock */}
              <div className="mt-8 rounded-2xl p-5 border mv-hairline" style={{ background: "#fff" }}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Lock size={16} className="mv-green-text" />
                  <div className="text-[11px] uppercase tracking-[0.22em] mv-green-text">90-Day Price Lock</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: "rgba(212,175,55,0.18)", color: "#8a6b12" }}>Free</span>
                </div>
                <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
                  Fix this quote for 90 days while you arrange your funds. No sudden increases — even if market prices move.
                </div>
                {lockRes ? (
                  <div className="mt-4 flex items-start gap-2 text-[14px]" data-testid="lock-result">
                    <Check size={18} className="mt-0.5" style={{ color: "#0A7A2C" }}/>
                    <div>
                      <div className="font-semibold mv-green-text">Locked until {String(lockRes.expires_at).slice(0,10)}</div>
                      <div className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>Reference · {lockRes.lock_ref}</div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={lock} disabled={lockBusy}
                    className="btn-green mt-4 disabled:opacity-60"
                    data-testid="calc-lock-btn"
                  >
                    <Lock size={16}/> {lockBusy ? "Locking…" : "Lock This Price For 90 Days"}
                  </button>
                )}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/register-interest" className="btn-gold" data-testid="calc-register-btn">Open An Account <ArrowRight size={16}/></Link>
                <Link to="/pay?type=deposit" className="btn-ghost" data-testid="calc-pay-deposit-btn">Pay Deposit Now</Link>
              </div>
              <div className="mt-4 text-[12px] font-accent italic flex items-center gap-2" style={{ color: "var(--mv-muted)" }}>
                <Shield size={12} style={{ color: "var(--mv-gold)" }} /> Illustrative only — not an offer, not a financial promotion, not a regulated mortgage quote. Final numbers live in your signed project contract.
              </div>
            </motion.div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
